/**
 * Shared tariff-by-date logic for both the dashboard mini calculator
 * (CalculatorCore) and the full Calculator page.
 * Keep this in sync: any change to tariff selection or expected departure
 * date behavior must work in both calculators via this module.
 */

import dayjs from "dayjs";
import type { Tariff } from "../hooks/useTariffs";

export function isTariffValidOnDate(
  tariff: { from: string | null; to: string | null },
  date: dayjs.Dayjs,
): boolean {
  const d = date.startOf("day");
  if (tariff.from && dayjs(tariff.from).startOf("day").isAfter(d))
    return false;
  if (tariff.to && dayjs(tariff.to).startOf("day").isBefore(d)) return false;
  return true;
}

/**
 * Returns true if at least one tariff is currently active or has a future start date.
 * Used to filter out routes that only have expired tariffs (e.g. old Shanghai–Rotterdam).
 */
export function hasActiveOrFutureTariff(
  tariffs: Array<{ from: string | null; to: string | null }>,
  referenceDate: dayjs.Dayjs,
): boolean {
  const d = referenceDate.startOf("day");
  return tariffs.some((t) => {
    if (isTariffValidOnDate(t, d)) return true;
    if (t.from && dayjs(t.from).startOf("day").isAfter(d)) return true;
    return false;
  });
}

export interface GetSelectedTariffParams {
  tariffs: Tariff[];
  selectedRoute: string | null;
  transportTypeId: number | null;
  bookingType: "LCL" | "FCL" | null;
  expectedDepartureDate: dayjs.Dayjs;
  volume: number;
  containerSize?: number | null;
}

export interface GetSelectedTariffResult {
  selectedTariff: Tariff | null;
  isTariffFallback: boolean;
}

/**
 * Returns the tariff to use for the given route and expected departure date.
 * Prefers a tariff valid on that date; if none, uses the closest rate period
 * (nearest future validity start, else nearest past end). For LCL, that period
 * includes every bracket row for that start/end so CBM tier selection still applies.
 * Also returns whether a fallback was used.
 */
export function getSelectedTariffForRoute(
  params: GetSelectedTariffParams,
): GetSelectedTariffResult {
  const {
    tariffs,
    selectedRoute,
    transportTypeId,
    bookingType,
    expectedDepartureDate,
    volume,
    containerSize,
  } = params;

  if (!selectedRoute || !transportTypeId) {
    return { selectedTariff: null, isTariffFallback: false };
  }

  const parts = selectedRoute.split("-");
  const originId = parts[0] ? parseInt(parts[0], 10) : NaN;
  const destId = parts[1] ? parseInt(parts[1], 10) : NaN;
  if (!Number.isFinite(originId) || !Number.isFinite(destId)) {
    return { selectedTariff: null, isTariffFallback: false };
  }

  const date = expectedDepartureDate.startOf("day");

  const findClosestTariff = (routeTariffs: Tariff[]): Tariff | null => {
    const future = routeTariffs
      .filter((t) => t.from && dayjs(t.from).isAfter(date))
      .sort((a, b) => dayjs(a.from!).valueOf() - dayjs(b.from!).valueOf());
    if (future.length > 0) return future[0];
    const past = routeTariffs
      .filter((t) => t.to && dayjs(t.to).isBefore(date))
      .sort((a, b) => dayjs(b.to!).valueOf() - dayjs(a.to!).valueOf());
    if (past.length > 0) return past[0];
    return routeTariffs.length > 0 ? routeTariffs[0] : null;
  };

  /**
   * When no tariff is valid on the chosen date, LCL needs every row for the *same*
   * nearest rate period (all CBM brackets share one validity window). A single
   * `findClosestTariff` row was arbitrary and ignored volume brackets.
   */
  const closestLclCandidatesByDate = (routeTariffs: Tariff[]): Tariff[] => {
    const future = routeTariffs.filter(
      (t) => t.from && dayjs(t.from).startOf("day").isAfter(date),
    );
    if (future.length > 0) {
      const minFrom = Math.min(
        ...future.map((t) => dayjs(t.from!).startOf("day").valueOf()),
      );
      return future.filter(
        (t) => dayjs(t.from!).startOf("day").valueOf() === minFrom,
      );
    }
    const past = routeTariffs.filter(
      (t) => t.to && dayjs(t.to).startOf("day").isBefore(date),
    );
    if (past.length > 0) {
      const maxTo = Math.max(
        ...past.map((t) => dayjs(t.to!).startOf("day").valueOf()),
      );
      return past.filter(
        (t) => dayjs(t.to!).startOf("day").valueOf() === maxTo,
      );
    }
    return [...routeTariffs];
  };

  const isLCL = transportTypeId === 2 && bookingType === "LCL";
  const isFCL = transportTypeId === 2 && bookingType === "FCL";

  let selectedTariff: Tariff | null = null;

  if (isLCL) {
    const routeTariffs = tariffs.filter(
      (t) =>
        t.transport_type?.id === 2 &&
        t.bookings_type?.id === 1 &&
        t.origin_port?.id === originId &&
        t.destination_port?.id === destId,
    );
    if (routeTariffs.length === 0)
      return { selectedTariff: null, isTariffFallback: false };

    const validOnDate = routeTariffs.filter((t) =>
      isTariffValidOnDate(t, expectedDepartureDate),
    );
    const candidates =
      validOnDate.length > 0
        ? validOnDate
        : closestLclCandidatesByDate(routeTariffs);
    if (candidates.length === 0)
      return { selectedTariff: null, isTariffFallback: false };

    const sorted = [...candidates].sort((a, b) => {
      if (!a.from) return 1;
      if (!b.from) return -1;
      return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
    });
    const withBracket = sorted.filter((t) => t.tariff_bracket);
    const inBracket = (from: number, to: number): boolean => {
      const high = Number(to);
      const useInclusiveUpper = high >= 999 || !Number.isFinite(high);
      return (
        volume >= Number(from) &&
        (useInclusiveUpper ? volume <= high : volume < high)
      );
    };
    /** When several brackets overlap (e.g. 0–∞ vs 5–10), pick the most specific tier (highest `from`). */
    const matchingForVolume = withBracket.filter((t) => {
      const bracket = t.tariff_bracket;
      if (!bracket) return false;
      return inBracket(bracket.from ?? 0, bracket.to ?? 999);
    });
    const matching =
      matchingForVolume.length === 0
        ? null
        : matchingForVolume.reduce((best, t) => {
            const bestFrom = Number(best.tariff_bracket?.from ?? 0);
            const candFrom = Number(t.tariff_bracket?.from ?? 0);
            return candFrom > bestFrom ? t : best;
          });
    selectedTariff = matching || sorted[0];
  } else if (isFCL) {
    const routeTariffs = tariffs.filter(
      (t) =>
        t.transport_type?.id === 2 &&
        t.bookings_type?.id === 2 &&
        t.origin_port?.id === originId &&
        t.destination_port?.id === destId,
    );
    if (routeTariffs.length === 0)
      return { selectedTariff: null, isTariffFallback: false };

    const requestedSize = containerSize != null ? Number(containerSize) : null;

    if (requestedSize != null) {
      // FCL with container size: only consider tariffs that match the selected size.
      // Then pick best by date (valid on date first, then closest).
      const matchingSize = routeTariffs.filter(
        (t) =>
          t.container_size != null &&
          Number(t.container_size.id) === requestedSize,
      );
      if (matchingSize.length > 0) {
        const validOnDate = matchingSize.filter((t) =>
          isTariffValidOnDate(t, expectedDepartureDate),
        );
        const closest = findClosestTariff(matchingSize);
        const candidates =
          validOnDate.length > 0 ? validOnDate : closest ? [closest] : [];
        const sorted = [...candidates].sort((a, b) => {
          if (!a.from) return 1;
          if (!b.from) return -1;
          return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
        });
        selectedTariff = sorted[0];
      }
    }

    if (selectedTariff == null) {
      // No container size, or no tariff matched the size: use previous logic (all route tariffs, pick by date).
      const validOnDate = routeTariffs.filter((t) =>
        isTariffValidOnDate(t, expectedDepartureDate),
      );
      const closestFcl = findClosestTariff(routeTariffs);
      const candidatesFcl =
        validOnDate.length > 0 ? validOnDate : closestFcl ? [closestFcl] : [];
      if (candidatesFcl.length === 0)
        return { selectedTariff: null, isTariffFallback: false };
      const sorted = [...candidatesFcl].sort((a, b) => {
        if (!a.from) return 1;
        if (!b.from) return -1;
        return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
      });
      selectedTariff = requestedSize != null
        ? sorted.find(
            (t) =>
              t.container_size != null &&
              Number(t.container_size.id) === requestedSize,
          ) ?? sorted[0]
        : sorted[0];
    }
  } else {
    const routeTariffs = tariffs.filter(
      (t) =>
        t.transport_type?.id === 1 &&
        t.origin_port?.id === originId &&
        t.destination_port?.id === destId,
    );
    if (routeTariffs.length === 0)
      return { selectedTariff: null, isTariffFallback: false };

    const validOnDate = routeTariffs.filter((t) =>
      isTariffValidOnDate(t, expectedDepartureDate),
    );
    const closestAir = findClosestTariff(routeTariffs);
    const candidatesAir =
      validOnDate.length > 0 ? validOnDate : closestAir ? [closestAir] : [];
    if (candidatesAir.length === 0)
      return { selectedTariff: null, isTariffFallback: false };

    const sorted = [...candidatesAir].sort((a, b) => {
      if (!a.from) return 1;
      if (!b.from) return -1;
      return dayjs(b.from).valueOf() - dayjs(a.from).valueOf();
    });
    selectedTariff = sorted[0];
  }

  const isTariffFallback =
    selectedTariff !== null &&
    !isTariffValidOnDate(selectedTariff, expectedDepartureDate);

  return { selectedTariff, isTariffFallback };
}
